import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

type ActiveGame = {
  id: number;
  bet_amount: number;
  mines_count: number;
  current_multiplier: number;
  revealed_tiles: number[];
};

const MinesScreen = () => {
  const { t } = useLanguage();
  const userId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
  
  const [balance, setBalance] = useState<number>(0);
  const [betAmount, setBetAmount] = useState<string>('1');
  const [minesCount, setMinesCount] = useState<number>(7);
  const [activeGame, setActiveGame] = useState<ActiveGame | null>(null);
  const [revealedTiles, setRevealedTiles] = useState<number[]>([]);
  const [minesPositions, setMinesPositions] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<'win' | 'loss' | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadBalance();
    loadActiveGame();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBalance = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/balance`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      setBalance(response.data.balance);
    } catch (err: any) {
      console.error('Error loading balance:', err);
    }
  };

  const loadActiveGame = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/games/mines/active`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      if (response.data.game) {
        setActiveGame(response.data.game);
        setRevealedTiles(response.data.game.revealed_tiles);
      }
    } catch (err: any) {
      console.error('Error loading active game:', err);
    }
  };

  const handleStartGame = async () => {
    setError('');
    setShowResult(false);
    setMinesPositions([]);
    setLastResult(null);

    const betNum = parseFloat(betAmount);
    if (isNaN(betNum) || betNum < 0.1) {
      setError(t.minBetError);
      return;
    }

    if (betNum > balance) {
      setError(t.insufficientFunds);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/games/mines/start`, {
        wallet_address: userId,
        bet_amount: betNum,
        mines_count: minesCount
      }, { timeout: 30000 });

      setActiveGame({
        id: response.data.game_id,
        bet_amount: betNum,
        mines_count: minesCount,
        current_multiplier: 1.0,
        revealed_tiles: []
      });
      setRevealedTiles([]);
      setBalance(response.data.new_balance);
    } catch (err: any) {
      console.error('Error starting game:', err);
      setError(err.response?.data?.detail || t.createGameError);
    } finally {
      setLoading(false);
    }
  };

  const handleRevealTile = async (index: number) => {
    if (!activeGame || loading || revealedTiles.includes(index)) return;

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/games/mines/reveal`, {
        wallet_address: userId,
        game_id: activeGame.id,
        tile_index: index
      }, { timeout: 30000 });

      if (response.data.result === 'loss') {
        // Попал на мину
        setRevealedTiles(response.data.revealed_tiles || revealedTiles);
        setMinesPositions(response.data.mines_positions);
        setShowResult(true);
        setLastResult('loss');
        setActiveGame(null);
        setBalance(response.data.new_balance);
      } else {
        // Безопасная клетка
        setRevealedTiles(response.data.revealed_tiles);
        setActiveGame({
          ...activeGame,
          current_multiplier: response.data.current_multiplier,
          revealed_tiles: response.data.revealed_tiles
        });
      }
    } catch (err: any) {
      console.error('Error revealing tile:', err);
      setError(err.response?.data?.detail || t.revealError);
    } finally {
      setLoading(false);
    }
  };

  const handleCashout = async () => {
    if (!activeGame || loading) return;

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/games/mines/cashout`, {
        wallet_address: userId,
        game_id: activeGame.id
      }, { timeout: 30000 });

      setBalance(response.data.new_balance);
      setShowResult(true);
      setLastResult('win');
      setActiveGame(null);
      setRevealedTiles([]);
    } catch (err: any) {
      console.error('Error cashing out:', err);
      setError(err.response?.data?.detail || t.cashoutError);
    } finally {
      setLoading(false);
    }
  };

  const getTileClass = (index: number): string => {
    if (minesPositions.includes(index)) {
      return 'bg-red-500 text-white border-red-700';
    }
    if (revealedTiles.includes(index)) {
      return 'bg-green-500 text-white border-green-700';
    }
    return 'bg-gradient-to-br from-blue-400 to-blue-600 text-white hover:from-blue-500 hover:to-blue-700 border-blue-700';
  };

  const getTileContent = (index: number): string => {
    if (minesPositions.includes(index)) {
      return '💣';
    }
    if (revealedTiles.includes(index)) {
      return '💎';
    }
    return '?';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-cyan-600 pb-20 px-4 pt-6">
      {/* Header */}
      <div className="card bg-gradient-to-br from-blue-700/80 to-cyan-800/80 text-white text-center mb-4">
        <div className="text-3xl font-bold mb-2">💣 {t.gameMines}</div>
        <div className="text-sm opacity-90">{t.gameMinesDesc}</div>
        <div className="text-2xl font-bold mt-2">{balance.toFixed(2)} TON</div>
      </div>

      {/* Game Setup (when no active game) */}
      {!activeGame && !showResult && (
        <div className="card bg-white/90 mb-4">
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">{t.minesBetAmount}</label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              disabled={loading}
              className="w-full px-4 py-3 rounded-lg border-2 border-blue-300 focus:border-blue-500 outline-none text-lg font-bold"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">{t.minesCount}</label>
            <div className="grid grid-cols-3 gap-2">
              {[7, 10, 15].map(count => (
                <button
                  key={count}
                  onClick={() => setMinesCount(count)}
                  disabled={loading}
                  className={`py-3 rounded-lg font-bold transition-all ${
                    minesCount === count
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleStartGame}
            disabled={loading}
            className={`w-full py-4 rounded-lg font-bold text-xl transition-all ${
              loading
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg'
            }`}
          >
            {loading ? `⏳ ${t.processing || 'Loading...'}` : `▶️ ${t.minesStartGame}`}
          </button>
        </div>
      )}

      {/* Active Game */}
      {activeGame && (
        <div className="card bg-white/90 mb-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-3 rounded-lg">
              <div className="text-xs text-gray-700">{t.minesMultiplier}</div>
              <div className="text-2xl font-bold text-blue-700">{activeGame.current_multiplier.toFixed(2)}x</div>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-green-200 p-3 rounded-lg">
              <div className="text-xs text-gray-700">{t.minesPotentialWin}</div>
              <div className="text-2xl font-bold text-green-700">
                {(activeGame.bet_amount * activeGame.current_multiplier).toFixed(2)} TON
              </div>
            </div>
          </div>

          {/* Grid 5x5 */}
          <div className="grid grid-cols-5 gap-2 mb-4">
            {Array.from({ length: 25 }, (_, i) => (
              <button
                key={i}
                onClick={() => handleRevealTile(i)}
                disabled={loading || revealedTiles.includes(i)}
                className={`aspect-square rounded-lg border-2 text-2xl font-bold transition-all ${
                  getTileClass(i)
                } ${revealedTiles.includes(i) ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {getTileContent(i)}
              </button>
            ))}
          </div>

          <button
            onClick={handleCashout}
            disabled={loading || revealedTiles.length === 0}
            className={`w-full py-4 rounded-lg font-bold text-xl transition-all ${
              loading || revealedTiles.length === 0
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg'
            }`}
          >
            💰 {t.minesCashout} {(activeGame.bet_amount * activeGame.current_multiplier).toFixed(2)} TON
          </button>
        </div>
      )}

      {/* Result (after game ends) */}
      {showResult && (
        <div className={`card border-2 mb-4 ${
          lastResult === 'win'
            ? 'bg-green-100/90 border-green-500'
            : 'bg-red-100/90 border-red-500'
        }`}>
          <div className="text-center">
            <div className="text-4xl mb-3">
              {lastResult === 'win' ? '🎉' : '💥'}
            </div>
            <div className={`text-3xl font-bold mb-2 ${
              lastResult === 'win' ? 'text-green-700' : 'text-red-700'
            }`}>
              {lastResult === 'win' ? t.minesWin : t.minesExplosion}
            </div>
            <button
              onClick={() => {
                setShowResult(false);
                setRevealedTiles([]);
                setMinesPositions([]);
              }}
              className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold"
            >
              🔄 {t.minesStartGame}
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="card bg-red-600/30 border border-red-500 text-white mb-4">
          <div className="font-semibold">❌ {error}</div>
        </div>
      )}

      {/* How to Play */}
      {!activeGame && !showResult && (
        <>
          <div className="card bg-white/80 mb-4">
            <h3 className="font-bold text-lg mb-3">📖 {t.minesHowToPlay}</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>{t.minesStep1}</li>
              <li>{t.diceStep3}</li>
              <li>{t.minesStep3}</li>
              <li>{t.minesStep4}</li>
              <li>{t.minesStep5}</li>
            </ol>
          </div>

          {/* Multipliers Table */}
          <div className="card bg-gradient-to-br from-blue-50 to-cyan-50">
            <h3 className="font-bold text-lg mb-3">💎 Таблица множителей</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b-2 border-blue-300">
                    <th className="py-2 px-2 text-left font-bold">Мины</th>
                    <th className="py-2 px-2 text-center font-bold">Безопасных</th>
                    <th className="py-2 px-2 text-center font-bold">Min (1)</th>
                    <th className="py-2 px-2 text-center font-bold">Max (все)</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-blue-200">
                    <td className="py-2 px-2 font-semibold">7 💣</td>
                    <td className="py-2 px-2 text-center text-green-600 font-bold">18</td>
                    <td className="py-2 px-2 text-center">1.25x</td>
                    <td className="py-2 px-2 text-center text-green-600 font-bold">35x</td>
                  </tr>
                  <tr className="border-b border-blue-200">
                    <td className="py-2 px-2 font-semibold">10 💣</td>
                    <td className="py-2 px-2 text-center text-orange-600 font-bold">15</td>
                    <td className="py-2 px-2 text-center">1.45x</td>
                    <td className="py-2 px-2 text-center text-orange-600 font-bold">60x</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-semibold">15 💣</td>
                    <td className="py-2 px-2 text-center text-red-600 font-bold">10</td>
                    <td className="py-2 px-2 text-center">2.57x</td>
                    <td className="py-2 px-2 text-center text-red-600 font-bold">100x</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-600 mt-3">
              💡 <strong>Min</strong> - открыв 1 клетку. <strong>Max</strong> - открыв все безопасные клетки.
            </p>
            <p className="text-xs text-gray-600 mt-1">
              🎯 {t.minesRisk}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default MinesScreen;

